import React from 'react'
import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard,
  Upload,
  Database,
  Globe,
  FileWarning,
  FileText,
  History,
  Search,
  ShieldAlert,
  Building2,
  CalendarClock,
} from 'lucide-react'
import './Sidebar.css'

interface SidebarProps {
  isCollapsed: boolean
  onNavigate?: () => void
}

interface NavItemType {
  to: string
  icon: React.ReactNode
  label: string
  badge?: string
  exact?: boolean
}

const TOUR_BY_PATH: Record<string, string> = {
  '/': 'sidebar-dashboard',
  '/pcr-upload': 'sidebar-products',
  '/pcr-management': 'sidebar-products',
  '/compliance-check': 'sidebar-run-checks',
  '/scheduler': 'sidebar-scheduler',
  '/exception-report': 'sidebar-findings',
  '/check-history': 'sidebar-jobs',
  '/rules-engine': 'sidebar-rules',
  '/term-watcher': 'sidebar-term-watches',
  '/distributors': 'sidebar-distributors',
}

const navSections: Array<{ label: string; items: NavItemType[] }> = [
  {
    label: 'Overview',
    items: [{ to: '/', icon: <LayoutDashboard size={20} />, label: 'Dashboard', exact: true }],
  },
  {
    label: 'Product Register',
    items: [
      { to: '/pcr-upload', icon: <Upload size={20} />, label: 'PCR Upload' },
      { to: '/pcr-management', icon: <Database size={20} />, label: 'PCR Management' },
      { to: '/distributors', icon: <Building2 size={20} />, label: 'Suppliers & Distributors' },
    ],
  },
  {
    label: 'Compliance',
    items: [
      { to: '/compliance-check', icon: <Globe size={20} />, label: 'Website Check' },
      { to: '/scheduler', icon: <CalendarClock size={20} />, label: 'Scheduler' },
      { to: '/exception-report', icon: <FileWarning size={20} />, label: 'Exception Report' },
      { to: '/check-history', icon: <History size={20} />, label: 'Check History' },
    ],
  },
  {
    label: 'Governance',
    items: [
      { to: '/rules-engine', icon: <ShieldAlert size={20} />, label: 'Rules Engine' },
      { to: '/term-watcher', icon: <Search size={20} />, label: 'Term Watcher' },
      { to: '/distributor-action-reports', icon: <FileText size={20} />, label: 'Distributor Reports' },
    ],
  },
]

function tourAttrForNav(to: string): string | undefined {
  const path = to.split('?')[0]
  return TOUR_BY_PATH[path]
}

export default function Sidebar({ isCollapsed, onNavigate }: SidebarProps) {
  const renderItem = (item: NavItemType) => (
    <li key={item.to} className="sidebar-menu-item">
      <NavLink
        to={item.to}
        data-tour={tourAttrForNav(item.to)}
        className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
        onClick={onNavigate}
        end={item.exact || item.to === '/'}
      >
        <span className="sidebar-link-icon">{item.icon}</span>
        {!isCollapsed && (
          <span className="sidebar-link-text">{item.label}</span>
        )}
        {!isCollapsed && item.badge && <span className="sidebar-link-badge">{item.badge}</span>}
      </NavLink>
      {isCollapsed && (
        <span className="sidebar-tooltip">{item.label}</span>
      )}
    </li>
  )

  return (
    <aside className={`app-sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      <nav className="sidebar-nav" data-tour="sidebar-nav">
        <ul className="sidebar-menu">
          {navSections.map((section) => (
            <React.Fragment key={section.label}>
              {!isCollapsed && <li className="sidebar-section-label">{section.label}</li>}
              {section.items.map(renderItem)}
            </React.Fragment>
          ))}
        </ul>
      </nav>

      <div className="sidebar-footer">
        {!isCollapsed && (
          <div className="sidebar-info">
            <span className="version">MIM GRC v1.0</span>
            <span className="phase">Phase 1 MVP</span>
          </div>
        )}
      </div>
    </aside>
  )
}
